import { authService } from '../../../services/auth.ts';
import { icons } from '../../../components/library/ui/icons.ts';
import { ToastService } from '../../../services/toast.ts';
import '../../../components/library/ui/app-card.ts';
import '../../../components/library/ui/app-button.ts';
import '../../../components/library/ui/app-save-bar.ts';
import '../../../components/library/ui/sub-nav.ts';
import '../../../components/library/ui/app-icon.ts';
import { SubNav } from '../../../components/library/ui/sub-nav.ts';
import { AppSaveBar } from '../../../components/library/ui/app-save-bar.ts';
import { APIKeyService, APIKey } from '../../../services/apikey.ts';
import '../../../components/library/ui/app-modal.ts';

export class AppProfile extends HTMLElement {
  private _profile: any = null;
  private _isLoading = true;
  private _currentTab: 'general' | 'api-keys' = 'general';
  private _toastService = ToastService.getInstance();
  private _apiKeyService = APIKeyService.getInstance();
  private _apiKeys: APIKey[] = [];
  private _isKeysLoading = false;
  private _generatedKey: string | null = null;
  private _showCreateModal = false;
  private _editingKey: APIKey | null = null;
  private _isCreating = false;

  static get observedAttributes() {
    return ['tab'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'tab' && newValue && oldValue !== newValue) {
      this.setTab(newValue as any);
    }
  }

  constructor() {
    super();
  }
  async connectedCallback() {
    const initialTab = this.getAttribute('tab') as any;
    if (initialTab && ['general', 'api-keys'].includes(initialTab)) {
      this._currentTab = initialTab;
    }
    
    await this.loadProfile();
    this.render();
    requestAnimationFrame(() => this.emitPanel());
  }


  private async setTab(tab: 'general' | 'api-keys') {
    if (this._currentTab === tab) return;
    this._currentTab = tab;
    if (tab === 'api-keys') {
      await this.loadApiKeys();
    }
    this.render();
    this.emitPanel();
  }

  private emitPanel() {
    const tabs = [
      { id: 'general', name: 'Profile', icon: 'user' },
      { id: 'api-keys', name: 'API Keys', icon: 'key' }
    ];

    const subNav = document.createElement('app-sub-nav') as SubNav;
    subNav.items = tabs;
    subNav.activeId = this._currentTab;

    subNav.addEventListener('nav-change', ((e: CustomEvent) => {
      const tab = e.detail.id;
      window.dispatchEvent(new CustomEvent('navigate', { 
        detail: { view: 'profile', params: { tab } } 
      }));
      this.setTab(tab as any);
    }) as any);

    this.dispatchEvent(new CustomEvent('shell-panel-changed', {
      bubbles: true,
      composed: true,
      detail: { 
        panel: subNav,
        header: {
          title: 'User Settings',
          subtitle: 'Manage your personal account settings and security credentials'
        }
      }
    }));
  }

  private _renderTimeout: any = null;

  private render() {
    if (this._renderTimeout) return;
    this._renderTimeout = setTimeout(() => {
        this._renderInternal();
        this._renderTimeout = null;
    }, 0);
  }

  private _renderInternal() {
    if (this._isLoading) {
      this.innerHTML = `<div style="padding: 40px; color: var(--text-dim); font-family: 'JetBrains Mono', monospace;">FETCHING_PROFILE_DATA...</div>`;
      return;
    }

    if (!this.querySelector('.main-profile-container')) {
        this.innerHTML = `
            <style>
              .profile-container {
                padding: 12px 0 0 0;
                position: relative;
                width: 100%;
                margin: 0;
                animation: slideUp 0.4s ease-out;
              }
              .profile-header {
                margin-bottom: 40px;
              }
              .sketchy-card {
                position: relative;
                background: rgba(255,255,255,0.01);
                padding: 32px;
              }
              .sketchy-card::before {
                content: "";
                position: absolute;
                top: 0; left: 0; right: 0; bottom: 0;
                border: 1px solid var(--border-sketch);
                filter: url(#sketchy-profile);
                pointer-events: none;
                z-index: 1;
              }
            </style>

            <svg style="position: absolute; width: 0; height: 0;">
              <defs>
                <filter id="sketchy-profile" x="-10%" y="-10%" width="120%" height="120%">
                  <feTurbulence type="turbulence" baseFrequency="0.02" numOctaves="3" seed="8" result="noise" />
                  <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G" />
                </filter>
              </defs>
            </svg>

            <div class="profile-container" id="profile-form">
              <div class="main-profile-container"></div>
            </div>

            <div class="modal-container"></div>
            <app-save-bar id="profile-save-bar"></app-save-bar>
        `;
    }

    const mainContainer = this.querySelector('.main-profile-container')!;
    const modalContainer = this.querySelector('.modal-container')!;

    mainContainer.innerHTML = this._currentTab === 'general' ? this.renderGeneral() : this.renderApiKeysListOnly();

    this.renderModals(modalContainer);

    this.setupListeners();
  }

  private renderApiKeysListOnly() {
      return `
        <div class="api-keys-section">
          <div style="display: flex; justify-content: flex-end; align-items: center; margin-bottom: 24px;">
            <app-button variant="white" id="generate-key-btn" icon="plus" size="sm" data-tooltip="New API Key">
            </app-button>
          </div>

          ${this._isKeysLoading ? `
            <div style="padding: 40px; text-align: center; color: var(--text-dim); font-family: var(--font-mono);">SYNCING_KEYS...</div>
          ` : this._apiKeys.length === 0 ? `
            <app-card padding="lg">
              <div style="text-align: center; padding: 40px;">
                <div style="margin-bottom: 24px; display: flex; justify-content: center; opacity: 0.8;">
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" style="width: 120px; height: 120px;">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3L15.5 7.5z" 
                          style="filter: url(#sketchy-profile);" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </div>
                <p style="margin: 0; color: var(--text-muted); font-family: var(--font-sketch); font-size: 1.2rem;">No API keys found.</p>
                <p style="font-size: 0.8rem; color: var(--text-dim); margin-top: 12px; max-width: 400px; margin-left: auto; margin-right: auto; font-family: var(--font-mono);">
                  API keys allow you to interact with Crate programmatically from your CI/CD pipelines or local terminal.
                </p>
              </div>
            </app-card>
          ` : `
            <div style="display: grid; gap: 16px;">
              ${this._apiKeys.map(key => `
                <div class="sketch-border" style="padding: 20px; background: rgba(255,255,255,0.02); display: flex; justify-content: space-between; align-items: center;">
                  <div>
                    <div style="display: flex; align-items: center; gap: 12px;">
                      <div style="font-family: var(--font-sketch); font-size: 1.1rem; color: ${key.active ? 'var(--text-main)' : 'var(--text-dim)'}">${key.name}</div>
                      ${!key.active ? `<span style="font-family: var(--font-mono); font-size: 0.6rem; background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 4px; color: var(--text-dim);">INACTIVE</span>` : ''}
                    </div>
                    <div style="font-family: var(--font-mono); font-size: 0.8rem; color: var(--text-dim); margin-top: 4px;">
                      ck_${key.prefix}_********************************
                    </div>
                    <div style="font-family: var(--font-mono); font-size: 0.65rem; color: var(--text-dim); margin-top: 8px; opacity: 0.6;">
                      CREATED: ${new Date(key.created_at).toLocaleDateString()} ${key.last_used_at ? `| LAST USED: ${new Date(key.last_used_at).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  <div style="display: flex; gap: 8px;">
                    <app-button variant="ghost" class="edit-key-btn" data-id="${key.id}" data-tooltip="Edit Key">
                      <app-icon name="edit" size="18"></app-icon>
                    </app-button>
                    <app-button variant="ghost" class="delete-key-btn" data-id="${key.id}" data-tooltip="Delete Key">
                      <app-icon name="trash" size="18" style="color: #ef4444; opacity: 0.8;"></app-icon>
                    </app-button>
                  </div>
                </div>
              `).join('')}
            </div>
          `}
        </div>
      `;
  }

  private renderModals(modalContainer: Element) {
      if (this._currentTab !== 'api-keys') {
          modalContainer.innerHTML = '';
          return;
      }

      modalContainer.innerHTML = `
        <app-modal id="create-key-modal" ${this._showCreateModal ? 'open' : ''} ${this._generatedKey ? 'layout="full"' : ''}>
          <div slot="header" style="font-family: var(--font-sketch);">Create API Key</div>
          
          ${this._generatedKey ? `
            <div style="padding: 20px; text-align: center;">
              <div style="color: var(--accent); font-family: var(--font-mono); font-size: 0.8rem; margin-bottom: 16px; background: rgba(245, 158, 11, 0.1); padding: 12px; border: 1px dashed var(--accent);">
                IMPORTANT: This key will only be shown once. Copy it now!
              </div>
              <div style="position: relative; margin-bottom: 24px;">
                <input type="text" readonly value="${this._generatedKey}" id="new-key-value"
                       style="width: 100%; padding: 16px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono); font-size: 1rem; text-align: center;">
                <app-button variant="ghost" id="copy-new-key" style="margin-top: 12px;">
                  <app-icon name="copy" size="16"></app-icon> COPY TO CLIPBOARD
                </app-button>
              </div>
            </div>
          ` : `
            <div style="display: grid; gap: 20px; padding: 10px 0;">
              <div>
                <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">KEY NAME</label>
                <input type="text" id="new-key-name" placeholder="e.g. CI/CD Runner" 
                       style="width: 100%; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono);">
              </div>
              <div>
                <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">SCOPES</label>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: rgba(255,255,255,0.02); padding: 16px; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;">
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="read:secrets" checked>
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">read:secrets</span>
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="write:secrets">
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">write:secrets</span>
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="read:gateway" checked>
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">read:gateway</span>
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="write:gateway">
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">write:gateway</span>
                  </label>
                </div>
              </div>
            </div>
          `}
          
          <div slot="actions">
            ${this._generatedKey ? `
              <app-button variant="primary" id="close-generated-view">DONE</app-button>
            ` : `
              <app-button variant="ghost" id="cancel-create-key" icon="x"></app-button>
              <app-button variant="primary" id="confirm-create-key" icon="check" ${this._isCreating ? 'loading' : ''}></app-button>
            `}
          </div>
        </app-modal>

        <app-modal id="edit-key-modal" ${this._editingKey ? 'open' : ''}>
          <div slot="header" style="font-family: var(--font-sketch);">Edit API Key</div>
          
          <div style="display: grid; gap: 20px; padding: 10px 0;">
            <div>
              <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">KEY NAME</label>
              <input type="text" id="edit-key-name" value="${this._editingKey?.name || ''}" 
                     style="width: 100%; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono);">
            </div>
            <div style="display: flex; align-items: center; gap: 12px;">
              <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                <input type="checkbox" id="edit-key-active" ${this._editingKey?.active ? 'checked' : ''}>
                <span style="font-size: 0.8rem; font-family: var(--font-mono);">Active</span>
              </label>
            </div>
            <div style="color: var(--text-dim); font-size: 0.7rem; font-family: var(--font-mono); opacity: 0.6;">
              NOTE: Scopes cannot be modified after creation for security reasons.
            </div>
          </div>
          
          <div slot="actions">
            <app-button variant="ghost" id="cancel-edit-key" icon="x"></app-button>
            <app-button variant="primary" id="confirm-edit-key" icon="check"></app-button>
          </div>
        </app-modal>
      `;
  }


  private renderGeneral() {
    return `
      <div class="sketchy-card">
        <div style="display: flex; align-items: center; gap: 32px; margin-bottom: 40px;">
          <div style="width: 100px; height: 100px; border-radius: 50%; overflow: hidden; border: 2px solid var(--accent); padding: 4px; position: relative; z-index: 2;">
             <img src="${this._profile?.avatar_url || `https://www.gravatar.com/avatar/${this._profile?.gravatar || '000'}?d=identicon&s=200`}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%;">
          </div>
          <div style="position: relative; z-index: 2;">
            <div style="font-size: 1.5rem; font-weight: bold; color: var(--text-main); font-family: var(--font-sketch);">${this._profile?.display_name || this._profile?.name || this._profile?.details?.display_name || 'Anonymous User'}</div>
            <div style="color: var(--text-muted); margin-top: 4px; font-family: var(--font-mono); font-size: 0.9rem;">${this._profile?.email}</div>
          </div>
        </div>

        <div style="display: grid; gap: 24px; position: relative; z-index: 2;">
          <div>
            <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">DISPLAY NAME</label>
            <input type="text" id="display-name-input" value="${this._profile?.details?.display_name || ''}" 
                   style="width: 100%; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: 'JetBrains Mono', monospace;">
          </div>
        </div>
      </div>
    `;
  }

  private renderApiKeys() {
    return `
      <div class="api-keys-section">
        <div style="display: flex; justify-content: flex-end; align-items: center; margin-bottom: 24px;">
          <app-button variant="white" id="generate-key-btn" icon="plus" size="sm" data-tooltip="New API Key">
          </app-button>
        </div>

        ${this._isKeysLoading ? `
          <div style="padding: 40px; text-align: center; color: var(--text-dim); font-family: var(--font-mono);">SYNCING_KEYS...</div>
        ` : this._apiKeys.length === 0 ? `
          <app-card padding="lg">
            <div style="text-align: center; padding: 40px;">
              <div style="margin-bottom: 24px; display: flex; justify-content: center; opacity: 0.8;">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" style="width: 120px; height: 120px;">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3L15.5 7.5z" 
                        style="filter: url(#sketchy-profile);" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
              </div>
              <p style="margin: 0; color: var(--text-muted); font-family: var(--font-sketch); font-size: 1.2rem;">No API keys found.</p>
              <p style="font-size: 0.8rem; color: var(--text-dim); margin-top: 12px; max-width: 400px; margin-left: auto; margin-right: auto; font-family: var(--font-mono);">
                API keys allow you to interact with Crate programmatically from your CI/CD pipelines or local terminal.
              </p>
            </div>
          </app-card>
        ` : `
          <div style="display: grid; gap: 16px;">
            ${this._apiKeys.map(key => `
              <div class="sketch-border" style="padding: 20px; background: rgba(255,255,255,0.02); display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="font-family: var(--font-sketch); font-size: 1.1rem; color: ${key.active ? 'var(--text-main)' : 'var(--text-dim)'}">${key.name}</div>
                    ${!key.active ? `<span style="font-family: var(--font-mono); font-size: 0.6rem; background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 4px; color: var(--text-dim);">INACTIVE</span>` : ''}
                  </div>
                  <div style="font-family: var(--font-mono); font-size: 0.8rem; color: var(--text-dim); margin-top: 4px;">
                    ck_${key.prefix}_********************************
                  </div>
                  <div style="font-family: var(--font-mono); font-size: 0.65rem; color: var(--text-dim); margin-top: 8px; opacity: 0.6;">
                    CREATED: ${new Date(key.created_at).toLocaleDateString()} ${key.last_used_at ? `| LAST USED: ${new Date(key.last_used_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <div style="display: flex; gap: 8px;">
                  <app-button variant="ghost" class="edit-key-btn" data-id="${key.id}" data-tooltip="Edit Key">
                    <app-icon name="edit" size="18"></app-icon>
                  </app-button>
                  <app-button variant="ghost" class="delete-key-btn" data-id="${key.id}" data-tooltip="Delete Key">
                    <app-icon name="trash" size="18" style="color: #ef4444; opacity: 0.8;"></app-icon>
                  </app-button>
                </div>
              </div>
            `).join('')}
          </div>
        `}

        <app-modal id="create-key-modal" ${this._showCreateModal ? 'open' : ''} ${this._generatedKey ? 'layout="full"' : ''}>
          <div slot="header" style="font-family: var(--font-sketch);">Create API Key</div>
          
          ${this._generatedKey ? `
            <div style="padding: 20px; text-align: center;">
              <div style="color: var(--accent); font-family: var(--font-mono); font-size: 0.8rem; margin-bottom: 16px; background: rgba(245, 158, 11, 0.1); padding: 12px; border: 1px dashed var(--accent);">
                IMPORTANT: This key will only be shown once. Copy it now!
              </div>
              <div style="position: relative; margin-bottom: 24px;">
                <input type="text" readonly value="${this._generatedKey}" id="new-key-value"
                       style="width: 100%; padding: 16px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono); font-size: 1rem; text-align: center;">
                <app-button variant="ghost" id="copy-new-key" style="margin-top: 12px;">
                  <app-icon name="copy" size="16"></app-icon> COPY TO CLIPBOARD
                </app-button>
              </div>
            </div>
          ` : `
            <div style="display: grid; gap: 20px; padding: 10px 0;">
              <div>
                <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">KEY NAME</label>
                <input type="text" id="new-key-name" placeholder="e.g. CI/CD Runner" 
                       style="width: 100%; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono);">
              </div>
              <div>
                <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">SCOPES</label>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: rgba(255,255,255,0.02); padding: 16px; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;">
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="read:secrets" checked>
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">read:secrets</span>
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="write:secrets">
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">write:secrets</span>
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="read:gateway" checked>
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">read:gateway</span>
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                    <input type="checkbox" class="scope-checkbox" value="write:gateway">
                    <span style="font-size: 0.8rem; font-family: var(--font-mono);">write:gateway</span>
                  </label>
                </div>
              </div>
            </div>
          `}
          
          <div slot="actions">
            ${this._generatedKey ? `
              <app-button variant="primary" id="close-generated-view">DONE</app-button>
            ` : `
              <app-button variant="ghost" id="cancel-create-key" icon="x"></app-button>
              <app-button variant="primary" id="confirm-create-key" icon="check" ${this._isCreating ? 'loading' : ''}></app-button>
            `}
          </div>
        </app-modal>

        <app-modal id="edit-key-modal" ${this._editingKey ? 'open' : ''}>
          <div slot="header" style="font-family: var(--font-sketch);">Edit API Key</div>
          
          <div style="display: grid; gap: 20px; padding: 10px 0;">
            <div>
              <label style="display: block; margin-bottom: 8px; font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">KEY NAME</label>
              <input type="text" id="edit-key-name" value="${this._editingKey?.name || ''}" 
                     style="width: 100%; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono);">
            </div>
            <div style="display: flex; align-items: center; gap: 12px;">
              <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                <input type="checkbox" id="edit-key-active" ${this._editingKey?.active ? 'checked' : ''}>
                <span style="font-size: 0.8rem; font-family: var(--font-mono);">Active</span>
              </label>
            </div>
            <div style="color: var(--text-dim); font-size: 0.7rem; font-family: var(--font-mono); opacity: 0.6;">
              NOTE: Scopes cannot be modified after creation for security reasons.
            </div>
          </div>
          
          <div slot="actions">
            <app-button variant="ghost" id="cancel-edit-key" icon="x"></app-button>
            <app-button variant="primary" id="confirm-edit-key" icon="check"></app-button>
          </div>
        </app-modal>
      </div>
    `;
  }

  private setupListeners() {
    const form = this.querySelector('#profile-form') as HTMLElement;
    const saveBar = this.querySelector('#profile-save-bar') as AppSaveBar;

    if (form && saveBar) {
      if (this._profile && !saveBar.active) {
        saveBar.resetInitialState(form);
      }

      form.addEventListener('input', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('app-modal')) return;
        saveBar.checkDirty(form);
      });

      saveBar.addEventListener('save', async () => {
        const input = this.querySelector('#display-name-input') as HTMLInputElement;
        const displayName = input.value;
        
        try {
          saveBar.loading = true;
          await authService.updateProfile({ displayName });
          this._toastService.success('Profile updated successfully');
          await this.loadProfile(true);
          saveBar.resetInitialState(form);
        } catch (e: any) {
          this._toastService.error(`Failed to update profile: ${e.message}`);
        } finally {
          saveBar.loading = false;
        }
      });

      saveBar.addEventListener('cancel', () => {
        this.render(); // Re-render to reset fields to original profile data
      });

      const genBtn = this.querySelector('#generate-key-btn');
      if (genBtn) {
        genBtn.addEventListener('click', () => {
          this._showCreateModal = true;
          this._generatedKey = null;
          this.render();
        });
      }

      const modal = this.querySelector('#create-key-modal');
      if (modal) {
        modal.addEventListener('close', () => {
          this._showCreateModal = false;
          this._generatedKey = null;
          this.render();
        });

        const cancelBtn = this.querySelector('#cancel-create-key');
        if (cancelBtn) cancelBtn.addEventListener('click', () => {
          this._showCreateModal = false;
          this.render();
        });

        const confirmBtn = this.querySelector('#confirm-create-key');
        if (confirmBtn) confirmBtn.addEventListener('click', () => this.handleCreateKey());

        const copyBtn = this.querySelector('#copy-new-key');
        if (copyBtn) copyBtn.addEventListener('click', () => {
          const input = this.querySelector('#new-key-value') as HTMLInputElement;
          input.select();
          document.execCommand('copy');
          this._toastService.success('API Key copied to clipboard');
        });

        const doneBtn = this.querySelector('#close-generated-view');
        if (doneBtn) doneBtn.addEventListener('click', () => {
          this._showCreateModal = false;
          this._generatedKey = null;
          this.render();
        });
      }

      const editModal = this.querySelector('#edit-key-modal');
      if (editModal) {
        editModal.addEventListener('close', () => {
          this._editingKey = null;
          this.render();
        });

        const cancelBtn = this.querySelector('#cancel-edit-key');
        if (cancelBtn) cancelBtn.addEventListener('click', () => {
          this._editingKey = null;
          this.render();
        });

        const confirmBtn = this.querySelector('#confirm-edit-key');
        if (confirmBtn) confirmBtn.addEventListener('click', () => this.handleSaveEdit());
      }

      this.querySelectorAll('.edit-key-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const id = (btn as HTMLElement).getAttribute('data-id');
          if (id) this.handleEditKey(id);
        });
      });

      this.querySelectorAll('.delete-key-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const id = (btn as HTMLElement).getAttribute('data-id');
          if (id) this.handleDeleteKey(id);
        });
      });
    }
  }

  private async loadApiKeys() {
    this._isKeysLoading = true;
    this.render();
    try {
      this._apiKeys = await this._apiKeyService.list();
    } catch (e: any) {
      console.error('Failed to load API keys:', e);
    } finally {
      this._isKeysLoading = false;
      this.render();
    }
  }

  private async handleCreateKey() {
    const nameInput = this.querySelector('#new-key-name') as HTMLInputElement;
    const name = nameInput.value;
    if (!name) {
      this._toastService.error('Please enter a name for the API key');
      return;
    }

    const scopeInputs = this.querySelectorAll('.scope-checkbox:checked') as NodeListOf<HTMLInputElement>;
    const scopes = Array.from(scopeInputs).map(i => i.value);

    this._isCreating = true;
    this.render();

    try {
      const result = await this._apiKeyService.create(name, scopes);
      this._generatedKey = result.key;
      await this.loadApiKeys();
    } catch (e: any) {
      // Toast handled by ApiClient
    } finally {
      this._isCreating = false;
      this.render();
    }
  }

  private async handleEditKey(id: string) {
    this._editingKey = this._apiKeys.find(k => k.id === id) || null;
    this.render();
  }

  private async handleSaveEdit() {
    if (!this._editingKey) return;
    
    const nameInput = this.querySelector('#edit-key-name') as HTMLInputElement;
    const activeInput = this.querySelector('#edit-key-active') as HTMLInputElement;
    
    const name = nameInput.value;
    const active = activeInput.checked;

    try {
      await this._apiKeyService.update(this._editingKey.id, { name, active });
      this._editingKey = null;
      await this.loadApiKeys();
    } catch (e: any) {
      // Toast handled by ApiClient
    }
  }

  private async handleDeleteKey(id: string) {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) return;

    try {
      await this._apiKeyService.revoke(id);
      await this.loadApiKeys();
    } catch (e: any) {
      // Toast handled by ApiClient
    }
  }

  private async loadProfile(force = false) {
    this._isLoading = !force;
    if (!force) this.render();
    
    try {
      this._profile = await authService.getProfile(force);
    } catch (e: any) {
      // Toast handled by ApiClient
    } finally {
      this._isLoading = false;
      this.render();
    }
  }

}

if (!customElements.get('app-profile')) {
  customElements.define('app-profile', AppProfile);
}
